// src/pages/PaymentSuccess.jsx
import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";

export default function PaymentSuccess() {
  const { state } = useLocation();
  const { user, session } = useAuth();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const { t, i18n } = useTranslation();
  const language = String(i18n.resolvedLanguage || i18n.language || "fr").toLowerCase();
  const locale = language.startsWith("de") ? "de-DE" : language.startsWith("en") ? "en-US" : "fr-FR";
  useEffect(() => {
    const run = async () => {
      try {
        // On demande au backend d’enregistrer la facture et de nous la renvoyer
        const res = await fetch("/api/record-service-payment", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
          body: JSON.stringify({
            paymentId: state?.paymentId,           // PaymentIntent.id
            proposalId: state?.proposalDetails?.proposalId,
            context: {
              type: state?.proposalDetails?.proposalType || "museum",
              artworkTitle: state?.artworkTitle,
              institution: state?.institution,
            },
          }),
        });
        const json = await res.json();
        if (!res.ok || json.error) throw new Error(json.error || t("paymentSuccessPage.serverError"));
        setInv(json.invoice || null);
      } catch (e) {
        setErr(e.message);
      } finally {
        setLoading(false);
      }
    };
    if (state?.paymentId && user) run();
    else setLoading(false);
  }, [state, user, session, t]);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-2xl mx-auto px-6 py-16 text-center">
        <h1 className="text-4xl font-light mb-3">{t("payment.success.title")}</h1>
        <p className="text-gray-600 mb-10">
          {t("payment.success.subtitle")}
        </p>

        <div className="rounded-xl border p-6 text-left bg-gray-50">
          <div className="mb-4">
            <div className="text-sm text-gray-500">{t("payment.success.work.label")}</div>
            <div className="font-medium">{state?.artworkTitle || t("payment.success.work.fallback")}</div>
          </div>
          <div className="mb-4">
            <div className="text-sm text-gray-500">{t("payment.success.institution.label")}</div>
            <div className="font-medium">{state?.institution}</div>
          </div>

          {loading ? (
            <div className="text-gray-500">{t("payment.success.loadingInvoice")}</div>
          ) : err ? (
            <div className="text-red-600">{t("payment.success.invoiceError", { error: err })}</div>
          ) : inv ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-500">{t("payment.success.invoice.label")}</div>
                  <div className="font-medium">{inv.number || inv.stripe_invoice_id}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">{t("payment.success.invoice.amount")}</div>
                  <div className="font-medium">
                    {new Intl.NumberFormat(locale, {
                      style: "currency",
                      currency: String(inv.currency || "eur").toUpperCase(),
                    }).format((inv.amount_paid || inv.amount_due || 0) / 100)}
                  </div>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                {inv.invoice_pdf && (
                  <a className="px-4 py-2 rounded bg-black text-white" href={inv.invoice_pdf} target="_blank" rel="noreferrer">
                    {t("payment.success.invoice.downloadPdf")}
                  </a>
                )}
                {inv.hosted_invoice_url && (
                  <a className="px-4 py-2 rounded border" href={inv.hosted_invoice_url} target="_blank" rel="noreferrer">
                    {t("payment.success.invoice.viewOnline")}
                  </a>
                )}
              </div>
            </div>
          ) : (
            <div className="text-gray-500">{t("payment.success.invoice.none")}</div>
          )}
        </div>

        <div className="mt-10 flex gap-3 justify-center">
          <Link to="/profile" className="px-5 py-3 rounded border">{t("payment.success.button.profile")}</Link>
          <Link to="/invoices" className="px-5 py-3 rounded bg-black text-white">{t("payment.success.button.invoices")}</Link>
        </div>
      </div>
    </div>
  );
}
